import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { appComponentsStorage } from '@frameio/shared/storage';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '@frameio/utils';
import type { SearchHistory, SearchHistoryItem } from '../types';

const STORAGE_KEY = 'frame-io--search-history';
const MAX_RECENT_SEARCHES = 5;

export const useSearchHistory = () => {
  const activeAccountId = useActiveAccountId() || '';
  const storageDataRef = useRef<Record<string, SearchHistory>>(
    EMPTY_OBJECT as Record<string, SearchHistory>
  );
  const notifyRef = useRef<(() => void) | null>(null);

  const getSnapshot = useCallback(() => {
    return (
      storageDataRef.current[activeAccountId] || (EMPTY_ARRAY as SearchHistory)
    );
  }, [activeAccountId]);

  const subscribe = useCallback((onStoreChange: () => void) => {
    notifyRef.current = onStoreChange;

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) {
        storageDataRef.current = (appComponentsStorage.get(STORAGE_KEY) ||
          EMPTY_OBJECT) as Record<string, SearchHistory>;
        onStoreChange();
      }
    };

    storageDataRef.current = (appComponentsStorage.get(STORAGE_KEY) ||
      EMPTY_OBJECT) as Record<string, SearchHistory>;

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const searchHistory = useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => EMPTY_ARRAY as SearchHistory
  );

  const addRecentSearch = useCallback(
    (search: SearchHistoryItem) => {
      const trimmedSearchTerm = search.query?.trim();

      if (!activeAccountId || !trimmedSearchTerm) return;

      const allRecentSearches = storageDataRef.current;
      const activeAccountRecentSearches =
        allRecentSearches[activeAccountId] || (EMPTY_ARRAY as SearchHistory);

      if (
        activeAccountRecentSearches[0]?.query?.trim() === trimmedSearchTerm &&
        activeAccountRecentSearches[0]?.mode === search.mode
      )
        return;

      const filteredSearches = activeAccountRecentSearches.filter(
        (searchItem: SearchHistoryItem) =>
          searchItem.query?.trim() !== trimmedSearchTerm ||
          searchItem.mode !== search.mode
      );

      const updatedSearches = [search, ...filteredSearches].slice(
        0,
        MAX_RECENT_SEARCHES
      );

      const newStorageData = {
        ...allRecentSearches,
        [activeAccountId]: updatedSearches,
      };

      storageDataRef.current = newStorageData;
      appComponentsStorage.set(STORAGE_KEY, newStorageData);

      if (notifyRef?.current) {
        notifyRef.current();
      }
    },
    [activeAccountId]
  );

  const clearSearchHistory = useCallback(() => {
    if (!activeAccountId) return;

    const allRecentSearches = storageDataRef.current;
    const newStorageData = {
      ...allRecentSearches,
      [activeAccountId]: [],
    };

    storageDataRef.current = newStorageData;
    appComponentsStorage.set(STORAGE_KEY, newStorageData);

    if (notifyRef?.current) {
      notifyRef.current();
    }
  }, [activeAccountId]);

  const removeRecentSearch = useCallback(
    (search: SearchHistoryItem) => {
      const trimmedSearchTerm = search.query?.trim();

      if (!activeAccountId || !trimmedSearchTerm) return;

      const allRecentSearches = storageDataRef.current;
      const activeAccountRecentSearches =
        allRecentSearches[activeAccountId] || (EMPTY_ARRAY as SearchHistory);

      const filteredSearches = activeAccountRecentSearches.filter(
        (searchItem: SearchHistoryItem) =>
          searchItem.query?.trim() !== trimmedSearchTerm ||
          searchItem.mode !== search.mode
      );

      const newStorageData = {
        ...allRecentSearches,
        [activeAccountId]: filteredSearches,
      };

      storageDataRef.current = newStorageData;
      appComponentsStorage.set(STORAGE_KEY, newStorageData);

      if (notifyRef?.current) {
        notifyRef.current();
      }
    },
    [activeAccountId]
  );

  return useMemo(
    () => ({
      addRecentSearch,
      searchHistory,
      clearSearchHistory,
      removeRecentSearch,
    }),
    [addRecentSearch, searchHistory, clearSearchHistory, removeRecentSearch]
  );
};
